import React, { useState, useEffect } from 'react'
import { supabase } from './supabaseClient'

export default function AuthScreen({ isRecovery = false }) {
  const [mode, setMode] = useState(isRecovery ? 'new-password' : 'login')

  useEffect(() => {
    if (isRecovery) setMode('new-password')
  }, [isRecovery])
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')

  const clearMessages = () => { setError(''); setMessage(''); }

  const handleLogin = async () => {
    if (!email.trim() || !password.trim()) { setError('Email and password are required.'); return; }
    setLoading(true); clearMessages();
    const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password })
    if (error) setError(error.message)
    setLoading(false)
  }

  const handleSignup = async () => {
    if (!email.trim() || !password.trim()) { setError('Email and password are required.'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    setLoading(true); clearMessages();
    const { error } = await supabase.auth.signUp({ email: email.trim(), password })
    if (error) setError(error.message)
    else setMessage('Account created! Check your email to confirm, then log in.')
    setLoading(false)
  }

  const handleReset = async () => {
    if (!email.trim()) { setError('Enter your email address above.'); return; }
    setLoading(true); clearMessages();
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: window.location.origin,
    })
    if (error) setError(error.message)
    else setMessage('Password reset email sent. Check your inbox.')
    setLoading(false)
  }

  const handleNewPassword = async () => {
    if (newPassword.length < 6) { setError('Password must be at least 6 characters.'); return; }
    setLoading(true); clearMessages();
    const { error } = await supabase.auth.updateUser({ password: newPassword })
    if (error) {
      setError(error.message)
      setLoading(false)
    } else {
      await supabase.auth.signOut()
      setMode('login')
      setNewPassword('')
      setMessage('Password updated! Sign in with your new password.')
      setLoading(false)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key !== 'Enter') return
    if (mode === 'login') handleLogin()
    else if (mode === 'signup') handleSignup()
    else if (mode === 'reset') handleReset()
    else if (mode === 'new-password') handleNewPassword()
  }

  const inputStyle = {
    flex: 1,
    background: '#1A1D23',
    border: 'none',
    color: '#E8E9EC',
    padding: '14px 16px',
    fontSize: 15,
    outline: 'none',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    width: '100%',
    borderRadius: 10,
  }

  const inputWrapStyle = {
    display: 'flex',
    alignItems: 'center',
    background: '#1A1D23',
    border: '1px solid #2A2E38',
    borderRadius: 10,
    overflow: 'hidden',
    marginBottom: 14,
  }

  const eyeBtn = {
    background: 'none',
    border: 'none',
    color: '#6B7080',
    cursor: 'pointer',
    padding: '0 14px',
    fontSize: 13,
    flexShrink: 0,
  }

  const btnPrimary = {
    width: '100%',
    background: loading ? '#2A2E38' : 'linear-gradient(135deg, #00E5A0, #7C8CF8)',
    border: 'none',
    borderRadius: 10,
    color: loading ? '#6B7080' : '#0B0D11',
    padding: '14px',
    fontSize: 15,
    fontWeight: 700,
    cursor: loading ? 'not-allowed' : 'pointer',
    letterSpacing: 0.3,
  }

  const titles = {
    'login': 'Welcome back',
    'signup': 'Create your account',
    'reset': 'Reset password',
    'new-password': 'Set new password',
  }
  const subtitles = {
    'login': 'Sign in to access your dashboard',
    'signup': 'Get started with BlessedOps',
    'reset': "We'll send a reset link to your email",
    'new-password': 'Choose a strong new password',
  }
  const btnLabels = {
    'login': 'Sign In',
    'signup': 'Create Account',
    'reset': 'Send Reset Email',
    'new-password': 'Update Password',
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0B0D11',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px 20px',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    }}>
      <div style={{ width: '100%', maxWidth: 400 }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{
            fontSize: 28, fontWeight: 800,
            fontFamily: "'JetBrains Mono', monospace",
            letterSpacing: 2,
            background: 'linear-gradient(135deg, #00E5A0, #7C8CF8)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: 6,
          }}>BLESSEDOPS</div>
          <div style={{ fontSize: 13, color: '#6B7080', letterSpacing: 1 }}>COMMAND CENTER</div>
        </div>

        {/* Card */}
        <div style={{ background: '#13151A', border: '1px solid #1E2028', borderRadius: 16, padding: '32px 28px' }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#E8E9EC', marginBottom: 6 }}>{titles[mode]}</div>
          <div style={{ fontSize: 13, color: '#6B7080', marginBottom: 28 }}>{subtitles[mode]}</div>

          {/* New password field */}
          {mode === 'new-password' && (
            <div style={inputWrapStyle}>
              <input
                value={newPassword}
                onChange={e => { setNewPassword(e.target.value); clearMessages(); }}
                onKeyDown={handleKeyDown}
                placeholder="New password (min 6 characters)"
                type={showNewPassword ? 'text' : 'password'}
                style={inputStyle}
              />
              <button onClick={() => setShowNewPassword(v => !v)} style={eyeBtn}>
                {showNewPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          )}

          {/* Email */}
          {mode !== 'new-password' && (
            <div style={{ ...inputWrapStyle, marginBottom: 14 }}>
              <input
                value={email}
                onChange={e => { setEmail(e.target.value); clearMessages(); }}
                onKeyDown={handleKeyDown}
                placeholder="Email address"
                type="email"
                autoCapitalize="none"
                autoCorrect="off"
                style={inputStyle}
              />
            </div>
          )}

          {/* Password */}
          {(mode === 'login' || mode === 'signup') && (
            <div style={{ ...inputWrapStyle, marginBottom: 20 }}>
              <input
                value={password}
                onChange={e => { setPassword(e.target.value); clearMessages(); }}
                onKeyDown={handleKeyDown}
                placeholder="Password"
                type={showPassword ? 'text' : 'password'}
                style={inputStyle}
              />
              <button onClick={() => setShowPassword(v => !v)} style={eyeBtn}>
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          )}

          {/* Error / Success */}
          {error && (
            <div style={{ background: 'rgba(255,107,107,0.1)', border: '1px solid rgba(255,107,107,0.3)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#FF6B6B', marginBottom: 16, lineHeight: 1.5 }}>{error}</div>
          )}
          {message && (
            <div style={{ background: 'rgba(0,229,160,0.08)', border: '1px solid rgba(0,229,160,0.25)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#00E5A0', marginBottom: 16, lineHeight: 1.5 }}>{message}</div>
          )}

          {/* Primary button */}
          <button
            onClick={mode === 'login' ? handleLogin : mode === 'signup' ? handleSignup : mode === 'reset' ? handleReset : handleNewPassword}
            disabled={loading}
            style={btnPrimary}
          >
            {loading ? 'Please wait...' : btnLabels[mode]}
          </button>

          {/* Forgot password */}
          {mode === 'login' && (
            <button onClick={() => { setMode('reset'); clearMessages(); }} style={{ background: 'none', border: 'none', color: '#6B7080', fontSize: 13, cursor: 'pointer', marginTop: 14, padding: 0, display: 'block', width: '100%', textAlign: 'center' }}>
              Forgot password?
            </button>
          )}
        </div>

        {/* Toggle login/signup */}
        <div style={{ textAlign: 'center', marginTop: 20 }}>
          {mode === 'login' && (
            <span style={{ fontSize: 13, color: '#6B7080' }}>
              No account?{' '}
              <button onClick={() => { setMode('signup'); clearMessages(); }} style={{ background: 'none', border: 'none', color: '#00E5A0', fontSize: 13, cursor: 'pointer', fontWeight: 600, padding: 0 }}>Sign up</button>
            </span>
          )}
          {(mode === 'signup' || mode === 'reset') && (
            <span style={{ fontSize: 13, color: '#6B7080' }}>
              Already have an account?{' '}
              <button onClick={() => { setMode('login'); clearMessages(); }} style={{ background: 'none', border: 'none', color: '#00E5A0', fontSize: 13, cursor: 'pointer', fontWeight: 600, padding: 0 }}>Sign in</button>
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
